import { useState } from 'react';
import { EyeOff, Pencil, Trash2 } from 'lucide-react';
import type { CommentView, RetroSnapshot } from '@retro/shared';
import { COMMENT_MAX_LENGTH } from '@retro/shared';
import { useRetroStore } from '../../lib/retroStore.js';
import { Avatar } from '../ui.js';
import { BoardColumn, BoardTrack } from './BoardLayout.js';
import { CommentComposer } from './CommentComposer.js';

/** An editable card — only ever the viewer's own during this phase. */
function OwnCard({ comment }: { comment: CommentView }) {
  const send = useRetroStore((state) => state.send);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(comment.text);

  if (editing) {
    return (
      <article className="note">
        <textarea
          autoFocus
          rows={3}
          value={draft}
          onChange={(event) => setDraft(event.target.value.slice(0, COMMENT_MAX_LENGTH))}
          className="input resize-none"
          aria-label="Edit comment"
        />
        <div className="note-f justify-end">
          <button
            type="button"
            className="chip"
            onClick={() => {
              setDraft(comment.text);
              setEditing(false);
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            className="chip !border-brand-200 !bg-brand-100 !text-brand-700"
            disabled={!draft.trim()}
            onClick={() => {
              void send('comment:update', { commentId: comment.id, text: draft.trim() });
              setEditing(false);
            }}
          >
            Save
          </button>
        </div>
      </article>
    );
  }

  return (
    <article className="note">
      <div className="note-a">
        {comment.isAnonymous ? (
          <>
            <span className="anon-av" aria-hidden>
              <EyeOff size={14} />
            </span>
            You · anonymous
          </>
        ) : (
          <>
            <Avatar emoji={comment.author?.avatar ?? ''} name="You" size="sm" />
            You
          </>
        )}
      </div>
      <p className="note-t">{comment.text}</p>
      <div className="note-f">
        <span className="text-[12px] text-muted">Only you can see this</span>
        <span className="ml-auto flex gap-1.5">
          <button
            type="button"
            className="chip"
            onClick={() => setEditing(true)}
            aria-label="Edit comment"
          >
            <Pencil size={13} aria-hidden /> Edit
          </button>
          <button
            type="button"
            className="chip"
            onClick={() => void send('comment:delete', { commentId: comment.id })}
            aria-label="Delete comment"
          >
            <Trash2 size={13} aria-hidden /> Delete
          </button>
        </span>
      </div>
    </article>
  );
}

/**
 * Step 1. Everyone writes in private: the board shows your own cards plus a
 * count of how many others have arrived, which signals progress without
 * revealing content.
 */
export function CommentsPhase({ snapshot }: { snapshot: RetroSnapshot }) {
  const limit = snapshot.retro.settings.comments.maxCommentsPerParticipant;
  const myTotal = snapshot.comments.length;
  const atLimit = limit !== null && myTotal >= limit;

  return (
    <BoardTrack>
      {snapshot.columns.map((column, index) => {
        const mine = snapshot.comments.filter((comment) => comment.columnId === column.id);
        const hidden = snapshot.hiddenCommentCounts[column.id] ?? 0;

        return (
          <BoardColumn
            key={column.id}
            column={column}
            index={index}
            badge={<span className="badge">{mine.length + hidden}</span>}
            action={
              <CommentComposer
                columnId={column.id}
                disabled={atLimit}
                disabledReason={
                  limit !== null ? `You've used all ${limit} of your comments.` : undefined
                }
              />
            }
          >
            {hidden > 0 ? (
              <div
                className="hidden-count"
                title={`${hidden} comment${hidden === 1 ? '' : 's'} from others, hidden until the reveal`}
              >
                <EyeOff size={15} aria-hidden />
                {hidden} {hidden === 1 ? 'card' : 'cards'} from others · hidden until Group
              </div>
            ) : null}
            {mine.map((comment) => (
              <OwnCard key={comment.id} comment={comment} />
            ))}
          </BoardColumn>
        );
      })}
    </BoardTrack>
  );
}
